import type { SessionMutationResponse, SessionSummary } from "@herskin/contracts";

function revisionOf(session: SessionSummary) {
  return session.management?.revision ?? -1;
}

/**
 * REST 响应和 session.updated 可能相邻到达。只接受更新的 management revision，
 * 避免较慢的响应把实时通道已经送达的新状态覆盖掉。
 */
export function upsertManagedSession(sessions: SessionSummary[], incoming: SessionSummary) {
  const existing = sessions.find((session) => session.id === incoming.id);
  if (existing && revisionOf(existing) > revisionOf(incoming)) return sessions;
  return [incoming, ...sessions.filter((session) => session.id !== incoming.id)];
}

export function applySessionMutation(
  sessions: SessionSummary[],
  sessionId: string,
  mutation: SessionMutationResponse,
) {
  if (mutation.action === "deleted" || !mutation.session) {
    return sessions.filter((session) => session.id !== sessionId);
  }
  return upsertManagedSession(sessions, mutation.session);
}

export function applySessionUpdatedEvent(
  sessions: SessionSummary[],
  sessionId: string,
  action: unknown,
  session: SessionSummary | null,
) {
  if (action === "deleted" || session?.management?.lifecycle === "deleted") {
    return sessions.filter((item) => item.id !== sessionId);
  }
  return session ? upsertManagedSession(sessions, session) : sessions;
}

export function mutationNotice(mutation: SessionMutationResponse) {
  if (mutation.remoteEffect === "pending") {
    return { tone: "warning" as const, message: "已在 HerSkin 生效，等待与 Hermes 同步" };
  }
  if (mutation.alreadyApplied) {
    return { tone: "success" as const, message: "会话状态已经是最新，无需重复操作" };
  }
  const actionLabel = {
    renamed: "会话已重命名",
    archived: "会话已归档",
    restored: "会话已恢复",
    deleted: "会话已删除",
  }[mutation.action];
  if (mutation.remoteEffect === "applied") {
    return { tone: "success" as const, message: `${actionLabel}，并已与 Hermes 同步` };
  }
  return { tone: "success" as const, message: `${actionLabel}，Hermes 远端会话未受影响` };
}
